import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { Toast } from './components/Toast'
import { applyUpdate, onUpdateReady, updatePending } from './lib/pwa'
import { AppProvider, useApp } from './state/AppProvider'

const Onboarding = lazy(() => import('./pages/Onboarding'))
const Result = lazy(() => import('./pages/Result'))
const Home = lazy(() => import('./pages/Home'))
const Watch = lazy(() => import('./pages/Watch'))
const Vocabulary = lazy(() => import('./pages/Vocabulary'))
const Study = lazy(() => import('./pages/Study'))
const Review = lazy(() => import('./pages/Review'))
const Exam = lazy(() => import('./pages/Exam'))
const My = lazy(() => import('./pages/My'))
const AdminVodNew = lazy(() => import('./pages/admin/VodNew'))
const AdminVodReview = lazy(() => import('./pages/admin/VodReview'))
const AdminDictionary = lazy(() => import('./pages/admin/Dictionary'))
const AdminLevels = lazy(() => import('./pages/admin/Levels'))
const AdminReport = lazy(() => import('./pages/admin/Report'))

/** 어드민은 데스크톱 860px 레이아웃이라 모바일 셸을 쓰지 않는다. */
function Shell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const wide = pathname.startsWith('/admin')
  return <div className={`app-shell${wide ? ' app-shell--wide' : ''}`}>{children}</div>
}

function StatusBanner() {
  const { mode, error } = useApp()

  if (error) {
    return (
      <div className="state-card" role="alert" style={{ marginBottom: 0 }}>
        <span className="state-card__title">데이터를 불러오지 못했어요</span>
        <span className="state-card__body">
          {error}
          <br />
          Supabase 스키마가 아직 없다면 <code>supabase db push</code> 로 마이그레이션을 적용하거나,
          <code>.env.develop</code> 값을 비워 Demo mode 로 실행하세요.
        </span>
      </div>
    )
  }

  if (mode !== 'local') return null
  return (
    <div
      className="screen-subtitle"
      style={{ padding: '6px 22px 0', textAlign: 'center' }}
      role="status"
    >
      Demo mode · 로컬 시드 데이터로 동작 중 (.env.develop 에 Supabase 키를 넣으면 전환)
    </div>
  )
}

/**
 * 새 버전 알림. 하루에 여러 번 배포하므로, 사용자가 어제 빌드를 계속 쓰고 있을 수 있다.
 * 학습 도중에 화면을 뺏지 않고 누를 때만 넘어간다.
 */
/*
 * 진행 중이면 화면을 뺏지 않는다.
 *
 * 레벨 테스트·복습·실력 점검은 답이 메모리에만 있어 새로고침하면 사라지고, 플레이어는
 * 보던 자리를 잃는다. 이 화면들에서는 배너만 띄우고 사용자가 누를 때 넘어간다.
 */
const BUSY_ROUTES = ['/onboarding', '/exam', '/review', '/watch']

function UpdateBanner() {
  const { pathname } = useLocation()
  const [ready, setReady] = useState(updatePending)
  useEffect(() => onUpdateReady(() => setReady(true)), [])

  /*
   * 안전한 화면이면 스스로 넘어간다.
   *
   * 배너는 얇은 줄이라 놓치기 쉽다 — 실제로 크롬에 옛 빌드가 남아 v1 문항이 그대로
   * 보이는 일이 있었다. 그 사이 데이터(문항·리포트)는 새것이라 화면과 어긋난다.
   * 새로고침이라 보던 화면 그대로 돌아온다.
   */
  const busy = BUSY_ROUTES.some((r) => pathname.startsWith(r))
  useEffect(() => {
    if (!ready || busy) return
    const t = window.setTimeout(applyUpdate, 1200)
    return () => window.clearTimeout(t)
  }, [ready, busy])

  if (!ready) return null
  return (
    <button type="button" className="update-banner" onClick={applyUpdate}>
      {busy ? '새 버전이 있어요 · 지금 새로고침 ↻' : '새 버전으로 넘어가는 중…'}
    </button>
  )
}

function Fallback() {
  return <div className="screen" aria-busy="true" />
}

export default function App() {
  return (
    <AppProvider>
      <Shell>
        <StatusBanner />
        <UpdateBanner />
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/onboarding" replace />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/onboarding/result" element={<Result />} />
            <Route path="/home" element={<Home />} />
            <Route path="/study" element={<Study />} />
            <Route path="/review" element={<Review />} />
            <Route path="/exam" element={<Exam />} />
            <Route path="/watch/:videoId" element={<Watch />} />
            <Route path="/vocabulary" element={<Vocabulary />} />
            <Route path="/my" element={<My />} />
            <Route path="/admin/vod/new" element={<AdminVodNew />} />
            <Route path="/admin/vod/:videoId/review" element={<AdminVodReview />} />
            <Route path="/admin/dictionary" element={<AdminDictionary />} />
            <Route path="/admin/levels" element={<AdminLevels />} />
            <Route path="/admin/report" element={<AdminReport />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
        <Toast />
      </Shell>
    </AppProvider>
  )
}
